import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject, viewChild } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { OpenerScheme, OpenerSchemeStatus, SchemeLocationFilter, CampaignsSchemeLocationFilterAdminApiClient } from 'src/app/api/api-campaigns.service.generated';
import { CampaignBannerComponent } from '../../_/campaign-banner/campaign-banner.component';
import { OpenerSchemeRulesComponent } from '../opener-scheme-rules/opener-scheme-rules.component';
import { OpenerSchemeLocationFiltersComponent } from '../opener-scheme-location-filters/opener-scheme-location-filters.component';
import { OpenerSchemeConstraintComponent } from '../opener-scheme-constraint/opener-scheme-constraint.component';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ButtonComponent } from "wecheer-common";
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-opener-scheme',
  templateUrl: './opener-scheme.component.html',
  styleUrls: ['./opener-scheme.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatButtonModule,
    FlexLayoutModule,
    CampaignBannerComponent,
    OpenerSchemeRulesComponent,
    OpenerSchemeLocationFiltersComponent,
    OpenerSchemeConstraintComponent,
    ButtonComponent
]
})
export class OpenerSchemeComponent implements OnInit, OnChanges {
  private locationFilterClient = inject(CampaignsSchemeLocationFilterAdminApiClient);


  @Input() scheme: OpenerScheme;
  @Input() advertiserId: string;
  @Input() clonedLocationFilter: SchemeLocationFilter;
  @Output() openerSchemeSaved = new EventEmitter<OpenerScheme>();
  @Output() schemeLocationFilterSaved = new EventEmitter<SchemeLocationFilter>();
  @Output() openerSchemeRemoved = new EventEmitter<OpenerScheme>();

  readonly schemeBanner = viewChild<CampaignBannerComponent>('schemeBanner');
  readonly schemeRules = viewChild<OpenerSchemeRulesComponent>('schemeRules');
  readonly schemeLocationFilters = viewChild<OpenerSchemeLocationFiltersComponent>('schemeLocationFilters');
  readonly schemeConstraint = viewChild<OpenerSchemeConstraintComponent>('schemeConstraint');

  //scheme: OpenerScheme;
  schemeStatuses: { name: string, value?: OpenerSchemeStatus, index: number }[] = [];
  isSetFormValueDone = false;

  form = new FormGroup({
    schemeName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
    status: new FormControl(null, [Validators.required]),
    startDate: new FormControl(null, [Validators.required]),
    endDate: new FormControl(null, [Validators.required]),
    priority: new FormControl(null, [Validators.required, Validators.min(0)])
  }
  );

  saveSchemeStatus: number = 0; // 0: idle, 1: saving, 2: saved

  constructor() {
    this.getSchemeStatuses();
  }

  ngOnChanges() {
    setTimeout(async () => {
      if (!this.isSetFormValueDone) {
        if (this.scheme) {
          await this.setFormValue(this.scheme);
        }
        else {
          await this.setFormValue(undefined);
        }
        this.isSetFormValueDone = true;
      }
    }, 500);
  }

  async ngOnInit() {
  }

  setBanners() {
    if (this.scheme)
      this.schemeBanner().setBanners(this.scheme.banner);
  }

  setSchemeRules() {
    if (this.scheme)
      this.schemeRules().setFormValue(this.scheme.rewardRules);
  }

  async setLocationFilters() {
    if (!this.scheme) return;
    // If cloned location filter is provided, use it (cloning scenario)
    // Otherwise, fetch from API (normal edit scenario)
    if (this.clonedLocationFilter) {
      this.schemeLocationFilters().setFormValue(this.clonedLocationFilter);
    } else if (this.scheme.id) {
      const locationFilter = await lastValueFrom(this.locationFilterClient.getBySchemeId(this.scheme.id));
      this.schemeLocationFilters().setFormValue(locationFilter);
    } else {
      // New scheme, initialize with empty filter
      this.schemeLocationFilters().setFormValue(new SchemeLocationFilter({ filters: [] }));
    }
  }

  async setFormValue(model: OpenerScheme) {
    this.scheme = model;

    this.form.patchValue({
      schemeName: model.name,
      status: model.status,
      startDate: new Date(model.startDate),
      endDate: new Date(model.endDate),
      priority: model.priority
    });

    this.setBanners();
    this.setSchemeRules();
    await this.setLocationFilters();
    // Ensure viewChild components are fully initialized before saving
    setTimeout(() => {
      this.onSaveScheme(); // send the current scheme and filters to the parent
    }, 100);
  }

  getFormValue(): OpenerScheme {
    const f = this.form.value;

    const model = new OpenerScheme({
      ...this.scheme,
      name: f.schemeName,
      status: f.status,
      banner: this.schemeBanner().getBanners(),
      startDate: f.startDate,
      endDate: f.endDate,
      priority: f.priority,
      rewardRules: this.schemeRules().savedRules,
      constraint: this.schemeConstraint().getConstraint()
    });
    if (model.startDate)
      model.startDate = new Date(Date.UTC(model.startDate.getFullYear(), model.startDate.getMonth(), model.startDate.getDate()));
    if (model.endDate)
      model.endDate = new Date(Date.UTC(model.endDate.getFullYear(), model.endDate.getMonth(), model.endDate.getDate()));
    return model;
  }

  getLocationFilter(): SchemeLocationFilter {
    if (!this.scheme?.id) {
      return new SchemeLocationFilter({ schemeId: undefined, filters: [] });
    }

    try {
      const locationFiltersComponent = this.schemeLocationFilters();
      const filter = locationFiltersComponent?.getFormValue() || new SchemeLocationFilter({ filters: [] });
      filter.schemeId = this.scheme.id;
      return filter;
    } catch (error) {
      // ViewChild not ready yet, return empty filter with schemeId
      return new SchemeLocationFilter({ schemeId: this.scheme.id, filters: [] });
    }
  }

  private getSchemeStatuses(): void {
    for (const key in OpenerSchemeStatus)
      if (key)
        this.schemeStatuses.push({ name: key, value: OpenerSchemeStatus[key] as OpenerSchemeStatus, index: Object.keys(OpenerSchemeStatus).indexOf(key) + 1 });
  }

  async onSubmit() {
  }

  onSaveScheme() {
    this.saveSchemeStatus = 1;
    if (!this.form.valid) return;
    const openerScheme = this.getFormValue();
    this.openerSchemeSaved.next(openerScheme);
    const schemeLocationFilter = this.getLocationFilter();
    this.schemeLocationFilterSaved.next(schemeLocationFilter);
    setTimeout(() => {
      this.saveSchemeStatus = 2;
    }, 500);
  }

  onRemoveScheme() {
    const openerScheme = this.getFormValue();
    this.openerSchemeRemoved.next(openerScheme);
  }

  onConstraintFormChange(isValidForm: boolean) {
    if (!isValidForm)
      this.form.setErrors({ 'invalid': true });
    else this.form.setErrors(null);
  }
}
